import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined"
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined"
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined"
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined"
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined"
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Typography from "@mui/material/Typography"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Row, { RowButColumnOnMobile } from "../../../../common/components/Row"
import TextSkeleton from "../../../../common/components/TextSkeleton"
import { BorderedPaddedTopSection } from "../../../../common/components/TopSection"
import { trimLongString, useIsMobile } from "../../../../common/utils"
import {
  selectHasUnsavedChanges,
  selectIsSaving,
  updateSaveButtonClicked,
} from "../reducer"
import { WORKFLOW_STATUS } from "../utils"
import ActivateWorkflowDialog from "./ActivateWorkflowDialog"
import AddCandidatesDialog from "./AddCandidatesDialog"
import DeleteWorkflowDialog from "./DeleteWorkflowDialog"
import DuplicateWorkflowDialog from "./DuplicateWorkflowDialog"
import EditWorkflowDialog from "./EditWorkflowDialog"
import StopWorkflowDialog from "./StopWorkflowDialog"
import UnsavedChangesPreventLeaveDialog from "./UnsavedChangesPreventLeaveDialog"

export default function WorkflowToolbar({ workflow, setWorkflow, isLoading }) {
  const [editWorkflowDialogOpen, setEditWorkflowDialogOpen] = useState(false)
  const [duplicateWorkflowDialogOpen, setDuplicateWorkflowDialogOpen] =
    useState(false)
  const [deleteWorkflowDialogOpen, setDeleteWorkflowDialogOpen] =
    useState(false)
  const [workflowDeleted, setWorkflowDeleted] = useState(false)
  const [addCandidatesDialogOpen, setAddCandidatesDialogOpen] = useState(false)
  const [activateWorkflowDialogOpen, setActivateWorkflowDialogOpen] =
    useState(false)
  const [stopWorkfowDialogOpen, setStopWorkflowDialogOpen] = useState(false)
  const [mobileMenuRef, setMobileMenuRef] = useState(null)
  const dispatch = useDispatch()
  const workflowHasChanged = useSelector(selectHasUnsavedChanges)
  const isSaving = useSelector(selectIsSaving)
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const saveButtonDisabled = !workflowHasChanged
  const workflowIsDraftOrStopped =
    [WORKFLOW_STATUS.draft, WORKFLOW_STATUS.stopped].indexOf(workflow.status) >
    -1

  const UpdateWorkflowStateIcon = workflowIsDraftOrStopped
    ? CheckCircleOutlinedIcon
    : HighlightOffOutlinedIcon

  const updateWorkflowStateText = workflowIsDraftOrStopped
    ? "Activate"
    : "Deactivate"

  function handleSaveButtonClicked() {
    dispatch(updateSaveButtonClicked(true))
  }

  function handleEditButtonClicked() {
    setEditWorkflowDialogOpen(true)
  }

  function handleEditMenuItemClicked() {
    handleEditButtonClicked()
    closeMobileMenu()
  }

  function handleEditWorkflowDialogClosed(updatedWorkflow) {
    if (updatedWorkflow) {
      setWorkflow(updatedWorkflow)
    }
    setEditWorkflowDialogOpen(false)
  }

  function handleDuplicateButtonClicked() {
    setDuplicateWorkflowDialogOpen(true)
  }

  function handleDuplicateMenuItemClicked() {
    handleDuplicateButtonClicked()
    closeMobileMenu()
  }

  function handleAddCandidatesButtonClicked() {
    setAddCandidatesDialogOpen(true)
  }

  function handleAddCandidatesMenuItemClicked() {
    handleAddCandidatesButtonClicked()
    closeMobileMenu()
  }

  function handleAddCandidatesDialogClosed(updatedWorkflow) {
    if (updatedWorkflow) {
      setWorkflow(updatedWorkflow)
    }
    setAddCandidatesDialogOpen(false)
  }

  function handleUpdateStatusButtonClicked() {
    if (workflowIsDraftOrStopped) {
      setActivateWorkflowDialogOpen(true)
    } else {
      setStopWorkflowDialogOpen(true)
    }
  }

  function handleUpdateWorkflowStatusMenuItemClicked() {
    handleUpdateStatusButtonClicked()
    closeMobileMenu()
  }

  function handleUpdateStatusDialogClosed(updatedWorkflow) {
    if (updatedWorkflow) {
      setWorkflow(updatedWorkflow)
    }
    setActivateWorkflowDialogOpen(false)
    setStopWorkflowDialogOpen(false)
  }

  function handleDeleteButtonClicked() {
    setDeleteWorkflowDialogOpen(true)
  }

  function handleDeleteWorkflowDialogClosed(deleted) {
    setWorkflowDeleted(deleted)
    setDeleteWorkflowDialogOpen(false)
  }

  function handleDeleteMenuItemClicked() {
    handleDeleteButtonClicked()
    closeMobileMenu()
  }

  function closeMobileMenu() {
    setMobileMenuRef(null)
  }

  return (
    <BorderedPaddedTopSection
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <RowButColumnOnMobile
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          "& > *:nth-of-type(n + 1)": { ml: { xs: 0, sm: 1 } },
          width: { xs: "100%", sm: "initial" },
        }}
      >
        <IconButton
          variant="bordered"
          color="inherit"
          onClick={() => navigate("/workflows")}
          sx={{ display: { xs: "none", sm: "inline-flex" } }}
        >
          <KeyboardBackspaceOutlinedIcon />
        </IconButton>
        <Row
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography variant="h3">
            {isLoading ? (
              <TextSkeleton width={20} />
            ) : (
              trimLongString(workflow.name, isMobile ? 20 : 25)
            )}
          </Typography>
          <Row
            sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center" }}
          >
            {isSaving ? (
              <CircularProgress
                variant="indeterminate"
                color="primary"
                size={16}
                sx={{ mr: 0.5 }}
              />
            ) : (
              <IconButton
                size="small"
                color="inherit"
                disabled={saveButtonDisabled}
                onClick={handleSaveButtonClicked}
              >
                <SaveOutlinedIcon
                  variant={saveButtonDisabled ? "g400" : "g500"}
                />
              </IconButton>
            )}
            <IconButton
              size="small"
              color="inherit"
              onClick={(e) => setMobileMenuRef(e.currentTarget)}
            >
              <MoreVertOutlinedIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuRef}
              open={Boolean(mobileMenuRef)}
              onClose={closeMobileMenu}
              anchorOrigin={{
                horizontal: "center",
                vertical: "center",
              }}
              transformOrigin={{
                horizontal: "right",
                vertical: "top",
              }}
            >
              <MenuItem onClick={handleUpdateWorkflowStatusMenuItemClicked}>
                <ListItemIcon>
                  <UpdateWorkflowStateIcon />
                </ListItemIcon>
                <ListItemText>{updateWorkflowStateText}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleAddCandidatesMenuItemClicked}>
                <ListItemIcon>
                  <AddCircleOutlinedIcon />
                </ListItemIcon>
                <ListItemText>Add candidates</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleEditMenuItemClicked}>
                <ListItemIcon>
                  <EditOutlinedIcon />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleDuplicateMenuItemClicked}>
                <ListItemIcon>
                  <ContentCopyOutlinedIcon />
                </ListItemIcon>
                <ListItemText>Duplicate</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleDeleteMenuItemClicked}>
                <ListItemIcon>
                  <DeleteOutlinedIcon />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </Menu>
          </Row>
        </Row>
        {isMobile ? <EnrolledCandidateCountChip workflow={workflow} /> : null}
      </RowButColumnOnMobile>
      <Row
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          "& > *:not(:last-child)": { mr: 1 },
        }}
      >
        <EnrolledCandidateCountChip workflow={workflow} />
        <IconButton
          variant="bordered"
          color="error"
          onClick={handleDeleteButtonClicked}
        >
          <DeleteOutlinedIcon />
        </IconButton>
        <DeleteWorkflowDialog
          open={deleteWorkflowDialogOpen}
          onClose={handleDeleteWorkflowDialogClosed}
          workflow={workflow}
        />
        <IconButton
          variant="bordered"
          color="inherit"
          onClick={handleEditButtonClicked}
        >
          <EditOutlinedIcon />
        </IconButton>
        <EditWorkflowDialog
          workflow={workflow}
          isLoading={isLoading}
          open={editWorkflowDialogOpen}
          onClose={handleEditWorkflowDialogClosed}
        />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<ContentCopyOutlinedIcon />}
          onClick={handleDuplicateButtonClicked}
        >
          Duplicate
        </Button>
        <DuplicateWorkflowDialog
          open={duplicateWorkflowDialogOpen}
          onClose={() => setDuplicateWorkflowDialogOpen(false)}
          workflow={workflow}
          isLoading={isLoading}
        />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<PersonAddOutlinedIcon />}
          onClick={handleAddCandidatesButtonClicked}
        >
          Add Candidates
        </Button>
        <Button
          variant="contained"
          color={workflowIsDraftOrStopped ? "success" : "error"}
          startIcon={<UpdateWorkflowStateIcon />}
          onClick={handleUpdateStatusButtonClicked}
        >
          {updateWorkflowStateText}
        </Button>
        <LoadingButton
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          loading={isSaving}
          disabled={saveButtonDisabled}
          onClick={handleSaveButtonClicked}
        >
          Save
        </LoadingButton>
      </Row>
      <AddCandidatesDialog
        open={addCandidatesDialogOpen}
        onClose={handleAddCandidatesDialogClosed}
        workflow={workflow}
      />
      <ActivateWorkflowDialog
        open={activateWorkflowDialogOpen}
        onClose={handleUpdateStatusDialogClosed}
        workflow={workflow}
      />
      <StopWorkflowDialog
        open={stopWorkfowDialogOpen}
        onClose={handleUpdateStatusDialogClosed}
        workflow={workflow}
      />
      <UnsavedChangesPreventLeaveDialog workflowDeleted={workflowDeleted} />
    </BorderedPaddedTopSection>
  )
}

function EnrolledCandidateCountChip({ workflow }) {
  return (
    <Chip
      color="primary"
      variant="outlined"
      label={`Enrolled Candidates: ${
        workflow.enrolled_count ? workflow.enrolled_count.toLocaleString() : 0
      }`}
      sx={{
        fontWeight: 600,
        alignSelf: { xs: "flex-start", sm: "initial" },
        mt: { xs: 1, sm: 0 },
      }}
    />
  )
}
